import { Injectable } from '@angular/core';
import {
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { TranslationService } from '@providers/translation-new/translation.service';
import { Router } from '@angular/router';
import { ROUTES_ADMIN } from '@constants/routes';
import { LoginEndpoints } from '@enums/network.enum';
import { StorageService } from '@providers/storage/storage.service';
import { UserManagerService } from '@providers/user-manager/user-manager.service';
import { ModalService } from '@providers/modal-services/modal.service';
import { SessionDataProvider } from '@providers/session-data/session-data';
import { loginEndpoints } from '@data/network.data';
import { StorageKeys } from '@enums/storage.enum';
import { RequestErrors } from '@enums/status.enum';
import { MethodsGeneralService } from '@providers/methods-general/methods-general/methods-general';
import { LoginService } from 'src/endpoints/login.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private loginService: LoginService,
    private methodGenerals: MethodsGeneralService,
    private modalService: ModalService,
    private router: Router,
    private sessionData: SessionDataProvider,
    private storage: StorageService,
    private translation: TranslationService,
    private userManager: UserManagerService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return from(this.reviewMultipleSession(request.url)).pipe(
      switchMap(() => {
        if (
          (!this.loginService.unauthorized && !this.noRequest(request.url)) ||
          (request.url.includes(loginEndpoints[LoginEndpoints.idpRequest]) &&
            request.url.includes('/logout'))
        ) {
          if (!window.navigator.onLine) {
            return throwError(() => ({
              status: RequestErrors.NoConnection,
              error: { message: 'no connection' },
            }));
          } else {
            const { token, tokenType, subscriptionIdp } = this.sessionData;
            const { currentlanguage } = this.translation;
            let body = request.body ? request.body : {};
            if (request.method !== 'GET' && !request.url.includes('?')) {
              if (typeof body === 'object' && body !== null) {
                body = {
                  ...body,
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                };
              }
            }
            const authorization = this.methodGenerals.setAuthorizationRetry(request.headers.get('Authorization') ?? '', token, tokenType);
            return next.handle(
              this.methodGenerals.getHeaders(
                request,
                token,
                authorization,
                currentlanguage,
                this.userManager.currentUser?.sub ?? this.userManager.temporaryIdSub,
                subscriptionIdp?._id,
                subscriptionIdp?.managementGroup?._id,
                body
              )
            );
          }
        }
      })
    );
  }

  async reviewMultipleSession(url: string): Promise<void> {
    const userCached = await this.storage.getMyData(StorageKeys.COOKIE_NAME);
    const userEmailMemory = this.userManager.email;
    const subscriptionCached = await this.storage.getMyData(StorageKeys.SUBSCRIPTION_IDP);
    const subscriptionMemory = this.sessionData.subscriptionIdp;
    if (
      (userEmailMemory !== userCached?.email || subscriptionMemory?._id !== subscriptionCached?._id) &&
      this.router.url !== ROUTES_ADMIN.LOGIN_ROUTE &&
      !this.methodGenerals.isLoginEndpoint(url)
    ) {
      if (
        !Boolean(userEmailMemory) ||
        !Boolean(userCached?.email) ||
        !Boolean(subscriptionMemory?._id) ||
        !Boolean(subscriptionCached?._id)
      ) {
        // Si alguno de estos datos no existe, posiblemente quiere decir que se cerró sesión en otra pestaña,
        // por lo que se refresca la página para que el auth se encargue de botarlo al login
        location.reload();
      }
      const {
        actions: { understood },
        alerts: { anotherSessionTitle, anotherSessionDescription },
      } = this.translation.getLangMessagesByKeys(['actions', 'alerts']);
      await this.modalService.showAlert({
        title: anotherSessionTitle,
        message: anotherSessionDescription,
        btnSave: understood,
      });
      location.reload();
    }
  }

  private noRequest(url: string): boolean {
    if (this.router.url === ROUTES_ADMIN.LOGIN_ROUTE) {
      const requestFound = Object.entries(loginEndpoints).every(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, value]) => !url.includes(value)
      );
      if (requestFound) {
        return true;
      }
    }
    return false;
  }
}
